import axios from "axios";
import { CalendarIcon } from "lucide-react";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FaBuilding,
  FaUser,
  FaBriefcase,
  FaTriangleExclamation,
  FaFileSignature,
  FaUpload,
  FaXmark,
  FaClipboardCheck,
  FaReplyAll,
} from "react-icons/fa6";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../payslip/payslippicker.css";

const steps = [
  {
    id: 1,
    title: "Company\nInformation",
    mobileTitle: "Company\nInformation",
    icon: FaBuilding,
    fields: [
      "companyName",
      "companyEmail",
      "companyPhone",
      "companyWebsite",
      "companyAddress",
      "companyLogo",
    ],
  },
  {
    id: 2,
    title: "Employee\nInformation",
    mobileTitle: "Employee\nInformation",
    icon: FaUser,
    fields: [
      "employeeName",
      "employeeId",
      "employeePhone",
      "employeeEmail",
      "residentialAddress",
      "currentDesignation",
      "currentDepartment",
      "workLocation",
      "reportingManager",
    ],
  },
  {
    id: 3,
    title: "Notice\nDetails",
    mobileTitle: "Notice\nDetails",
    icon: FaTriangleExclamation,
    fields: [
      "noticeDate",
      "noticeCategory",
      "severityLevel",
      "referenceNumber",
      "previousCommunication",
    ],
  },
  {
    id: 4,
    title: "Incident\nDetails",
    mobileTitle: "Incident\nDetails",
    icon: FaClipboardCheck,
    fields: [
      "incidentDate",
      "incidentLocation",
      "incidentDescription",
      "allegationOrConcern",
      "relevantCompanyPolicy",
      "witnessDetails",
    ],
  },
  {
    id: 5,
    title: "Response\nRequirements",
    mobileTitle: "Response\nRequirements",
    icon: FaReplyAll,
    fields: [
      "explanationRequiredFor",
      "responseDeadline",
      "responseSubmissionTo",
      "responseFormat",
      "meetingOrHearingDetails",
    ],
  },
  {
    id: 6,
    title: "Possible\nAction",
    mobileTitle: "Possible\nAction",
    icon: FaBriefcase,
    fields: [
      "actionIfNoResponse",
      "possibleDisciplinaryAction",
      "additionalInstructions",
    ],
  },
  {
    id: 7,
    title: "Authorisation\nDetails",
    mobileTitle: "Authorisation\nDetails",
    icon: FaFileSignature,
    fields: [
      "issuedBy",
      "issuerDesignation",
      "placeOfIssue",
      "deliveryMethod",
      "acknowledgementRequired",
    ],
  },
];

const defaultValues = {
  // Company
  companyName: "",
  companyEmail: "",
  companyPhone: "",
  companyWebsite: "",
  companyAddress: "",
  companyLogo: null,

  // Employee
  employeeName: "",
  employeeId: "",
  employeePhone: "",
  employeeEmail: "",
  residentialAddress: "",
  currentDesignation: "",
  currentDepartment: "",
  workLocation: "",
  reportingManager: "",

  // Notice Details
  noticeDate: "",
  noticeCategory: "",
  severityLevel: "",
  referenceNumber: "",
  previousCommunication: "",

  // Incident Details
  incidentDate: "",
  incidentLocation: "",
  incidentDescription: "",
  allegationOrConcern: "",
  relevantCompanyPolicy: "",
  witnessDetails: "",

  // Response Requirements
  explanationRequiredFor: "",
  responseDeadline: "",
  responseSubmissionTo: "",
  responseFormat: "",
  meetingOrHearingDetails: "",

  // Possible Action
  actionIfNoResponse: "",
  possibleDisciplinaryAction: "",
  additionalInstructions: "",

  // Authorisation Details
  issuedBy: "",
  issuerDesignation: "",
  placeOfIssue: "",
  deliveryMethod: "",
  acknowledgementRequired: "",
};

const inputClass =
  "w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#10324F] outline-none transition placeholder:text-slate-400 focus:border-0 focus:ring-0";

const labelClass = "mb-1.5 block text-sm font-medium text-[#10324F]";

const FormField = ({ label, required, error, children }) => {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      {children}

      {error && (
        <p className="mt-1 text-[10px] text-red-500">{error.message}</p>
      )}
    </div>
  );
};

const parseDateValue = (value) => {
  if (!value) return null;

  const [year, month, day] = String(value).split("-").map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const formatDateValue = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const DateField = ({ name, label, required, control, errors }) => (
  <FormField label={label} required={required} error={errors[name]}>
    <Controller
      name={name}
      control={control}
      rules={
        required
          ? {
              required: `${label} is required`,
            }
          : {}
      }
      render={({ field }) => (
        <DatePicker
          selected={parseDateValue(field.value)}
          onChange={(date) => {
            field.onChange(formatDateValue(date));
          }}
          onBlur={field.onBlur}
          dateFormat="yyyy-MM-dd"
          showPopperArrow={false}
          isClearable
          placeholderText="Select date"
          showIcon
          toggleCalendarOnIconClick
          icon={<CalendarIcon className="h-4 w-4" />}
          className={`${inputClass} h-10`}
          wrapperClassName="w-full"
          popperClassName="payslip-datepicker-popper react-popper-offset"
          calendarClassName="payslip-datepicker"
          popperPlacement="top-start"
          portalId="datepicker-portal"
        />
      )}
    />
  </FormField>
);

const BASE_URL = import.meta.env.VITE_BASE_URL;

const ShowCauseNoticeGeneratorForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [logoPreview, setLogoPreview] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  const values = watch();

  const currentStep = steps[activeStep];

  const handleNext = async () => {
    const valid = await trigger(currentStep.fields);

    // Company logo is manually handled
    if (activeStep === 0 && !logoPreview) {
      setError("companyLogo", {
        type: "required",
        message: "Company logo is required",
      });

      return;
    }

    if (!valid) return;

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setValue("companyLogo", file, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    const url = URL.createObjectURL(file);
    setLogoPreview(url);
    clearErrors("companyLogo");
  };

  const clearLogo = () => {
    setLogoPreview("");
    setValue("companyLogo", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSubmit = async (data) => {
    try {
      if (isGenerating) return;

      setIsGenerating(true);

      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === "companyLogo") {
          if (value instanceof File) {
            formData.append("companyLogo", value);
          }
        } else {
          formData.append(key, value ?? "");
        }
      });

      const response = await axios.post(
        `${BASE_URL}/generate-showcauseletter`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );

      const contentType = response.headers["content-type"];

      if (!contentType?.includes("application/pdf")) {
        const text = await response.data.text();

        console.error("Backend returned:", text);

        throw new Error("Backend did not return a Show Cause Notice PDF");
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `Show-Cause-Notice-${data.employeeName || "Employee"}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.open(url, "_blank");

      //  reset(defaultValues);

      setLogoPreview("");

      setActiveStep(0);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch (error) {
      console.error("Show cause notice generation failed:", error);

      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();

        try {
          const errorData = JSON.parse(text);

          alert(errorData?.message || "Failed to generate Show Cause Notice");
        } catch {
          alert("Failed to generate Show Cause Notice");
        }
      } else {
        alert(error?.message || "Failed to generate Show Cause Notice");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <section>
      <form className="w-full mx-auto">
        <div className="mx-auto grid w-full grid-cols-1 gap-2 xl:grid-cols-[1.9fr_1fr]">
          {/* ================= FORM ================= */}
          <div className="min-w-0 rounded-xl border border-white  shadow-sm backdrop-blur-2xl p-4">
            {/* STEP NAVIGATION */}
            <div className="scrollbar-hide flex w-full gap-2 overflow-x-auto pb-2 sm:overflow-visible">
              {steps.map((step, index) => {
                const active = activeStep === index;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={async () => {
                      if (index <= activeStep) {
                        setActiveStep(index);
                        return;
                      }

                      const valid = await trigger(steps[activeStep].fields);

                      if (valid) {
                        setActiveStep(index);
                      }
                    }}
                    className={`flex min-w-26.25 shrink-0 flex-1 items-center justify-center rounded-md px-2 py-2.5 text-center transition                       
          sm:min-w-0 
          ${
            active
              ? " bg-[#10324F] text-white shadow-sm"
              : "border border-white  text-[#10324F] bg-linear-to-b from-white/60 to-[#D1DFEA]/30  shadow-sm hover:bg-white/70"
          }`}
                  >
                    <span className="whitespace-pre-line text-xs leading-4 sm:text-sm">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* CURRENT STEP */}
            <div className="mt-5">
              {activeStep === 0 && (
                <CompanyInformation
                  register={register}
                  errors={errors}
                  handleLogoChange={handleLogoChange}
                  logoPreview={logoPreview}
                  clearLogo={clearLogo}
                />
              )}

              {activeStep === 1 && (
                <EmployeeInformation register={register} errors={errors} />
              )}

              {activeStep === 2 && (
                <NoticeDetails
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 3 && (
                <IncidentDetails
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 4 && (
                <ResponseRequirements
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 5 && (
                <PossibleAction register={register} errors={errors} />
              )}

              {activeStep === 6 && (
                <AuthorisationDetails register={register} errors={errors} />
              )}
            </div>

            {/* NAVIGATION BUTTONS */}
            <div className="mt-7 flex items-center justify-center gap-3">
              {activeStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="min-w-30 rounded-md border border-[#163957] px-6 py-2.5 text-sm font-medium text-[#163957] transition hover:bg-[#163957] hover:text-white"
                >
                  Previous
                </button>
              )}

              {activeStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="min-w-30 rounded-md bg-[#163957] px-8 py-2.5 text-sm font-medium text-white transition hover:bg-[#10324F]"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isGenerating}
                  onClick={handleSubmit(onSubmit)}
                  className={`min-w-45 rounded-md px-8 py-2.5 text-sm font-medium text-white transition ${
                    isGenerating
                      ? "cursor-not-allowed bg-slate-400"
                      : "bg-[#10324F] hover:bg-[#10324F]"
                  }`}
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating...
                    </span>
                  ) : (
                    "Generate Show Cause Notice"
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ================= PREVIEW ================= */}
          <ShowCauseNoticePreview values={values} logoPreview={logoPreview} />
        </div>
      </form>
    </section>
  );
};

export default ShowCauseNoticeGeneratorForm;

const CompanyInformation = ({
  register,
  errors,
  handleLogoChange,
  logoPreview,
  clearLogo,
}) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Company Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Company Name" required error={errors.companyName}>
          <input
            {...register("companyName", {
              required: "Company name is required",
              minLength: {
                value: 2,
                message: "Enter a valid company name",
              },
            })}
            className={inputClass}
            placeholder="Enter Company name"
          />
        </FormField>

        <FormField label="Company Email" required error={errors.companyEmail}>
          <input
            type="email"
            {...register("companyEmail", {
              required: "Company email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Email"
          />
        </FormField>

        <FormField label="Phone Number" required error={errors.companyPhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("companyPhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Website" error={errors.companyWebsite}>
          <input
            {...register("companyWebsite", {
              required: "Company website is required",
              pattern: {
                value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
                message: "Enter valid website",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Website"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Company Address"
            required
            error={errors.companyAddress}
          >
            <input
              {...register("companyAddress", {
                required: "Company address is required",
              })}
              className={inputClass}
              placeholder="Enter Company Address"
            />
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField label="Company Logo" required error={errors.companyLogo}>
            {logoPreview ? (
              <div className="w-60">
                <div className="relative flex h-16 w-full items-center justify-center rounded-md border border-slate-200 bg-white">
                  <img
                    src={logoPreview}
                    alt="Company logo"
                    className="h-14 max-w-55 object-contain"
                  />

                  <button
                    type="button"
                    onClick={clearLogo}
                    className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600"
                    aria-label="Remove logo"
                  >
                    <FaXmark className="text-xs" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex h-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-white transition hover:bg-slate-50">
                <div className="text-center text-[10px] text-[#10324F]">
                  <FaUpload className="mx-auto mb-1 text-[#10324F]/30" />
                  Upload Logo
                </div>

                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </label>
            )}
          </FormField>
        </div>
      </div>
    </div>
  );
};

const EmployeeInformation = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Employee Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Employee Full Name"
          required
          error={errors.employeeName}
        >
          <input
            {...register("employeeName", {
              required: "Employee name is required",
            })}
            className={inputClass}
            placeholder="Enter name"
          />
        </FormField>

        <FormField label="Employee ID" required error={errors.employeeId}>
          <input
            {...register("employeeId", {
              required: "Employee ID is required",
            })}
            className={inputClass}
            placeholder="Enter ID"
          />
        </FormField>

        <FormField label="Phone Number" error={errors.employeePhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("employeePhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Email Address" error={errors.employeeEmail}>
          <input
            type="email"
            {...register("employeeEmail", {
              required: "Employee email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Email"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Residential address"
            required
            error={errors.residentialAddress}
          >
            <input
              {...register("residentialAddress", {
                required: "Residential address is required",
              })}
              className={inputClass}
              placeholder="Enter Address"
            />
          </FormField>
        </div>

        <FormField
          label="Current designation"
          required
          error={errors.currentDesignation}
        >
          <input
            {...register("currentDesignation", {
              required: "Current designation is required",
            })}
            className={inputClass}
            placeholder="Enter Cflex designation"
          />
        </FormField>

        <FormField label="Current Department" error={errors.currentDepartment}>
          <input
            {...register("currentDepartment")}
            className={inputClass}
            placeholder="Enter Department"
          />
        </FormField>

        <FormField label="Work Location" error={errors.workLocation}>
          <input
            {...register("workLocation")}
            className={inputClass}
            placeholder="Enter Work Location"
          />
        </FormField>

        <FormField
          label="Current reporting manager"
          error={errors.reportingManager}
        >
          <input
            {...register("reportingManager")}
            className={inputClass}
            placeholder="Enter Reporting manager"
          />
        </FormField>
      </div>
    </div>
  );
};

const NoticeDetails = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Notice Details
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <DateField
            name="noticeDate"
            label="Notice Date"
            required
            control={control}
            errors={errors}
          />
        </div>

        <FormField
          label="Notice Category"
          required
          error={errors.noticeCategory}
        >
          <select
            {...register("noticeCategory", {
              required: "Notice category is required",
            })}
            className={inputClass}
          >
            <option value="">Enter Notice Category</option>
            <option value="Attendance">Attendance</option>
            <option value="Punctuality">Punctuality</option>
            <option value="Performance">Performance</option>
            <option value="Misconduct">Misconduct</option>
            <option value="Insubordination">Insubordination</option>
            <option value="Policy Violation">Policy Violation</option>
            <option value="Workplace Behaviour">Workplace Behaviour</option>
            <option value="Other">Other</option>
          </select>
        </FormField>

        <FormField label="Severity Level" required error={errors.severityLevel}>
          <select
            {...register("severityLevel", {
              required: "Severity level is required",
            })}
            className={inputClass}
          >
            <option value="">Select Severity Level</option>
            <option value="Minor">Minor</option>
            <option value="Moderate">Moderate</option>
            <option value="Serious">Serious</option>
            <option value="Critical">Critical</option>
          </select>
        </FormField>

        <FormField
          label="Reference Number"
          required
          error={errors.referenceNumber}
        >
          <input
            {...register("referenceNumber", {
              required: "Reference number is required",
            })}
            className={inputClass}
            placeholder="Enter Reference Number"
          />
        </FormField>

        <FormField
          label="Previous Warning or Communication"
          error={errors.previousCommunication}
        >
          <select {...register("previousCommunication")} className={inputClass}>
            <option value="">Select option</option>
            <option value="None">None</option>
            <option value="Verbal Warning">Verbal Warning</option>
            <option value="Written Warning">Written Warning</option>
            <option value="Email Communication">Email Communication</option>
            <option value="Counselling Session">Counselling Session</option>
          </select>
        </FormField>
      </div>
    </div>
  );
};

const IncidentDetails = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Incident Details
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          name="incidentDate"
          label="Incident Date"
          required
          control={control}
          errors={errors}
        />

        <FormField label="Incident Location" error={errors.incidentLocation}>
          <input
            {...register("incidentLocation")}
            className={inputClass}
            placeholder="Enter Incident Location"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Incident Description"
            required
            error={errors.incidentDescription}
          >
            <textarea
              {...register("incidentDescription", {
                required: "Incident description is required",
              })}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter Incident Description"
            />
          </FormField>
        </div>

        <FormField
          label="Allegation or Concern"
          required
          error={errors.allegationOrConcern}
        >
          <input
            {...register("allegationOrConcern", {
              required: "Allegation or concern is required",
            })}
            className={inputClass}
            placeholder="Enter Allegation or Concern"
          />
        </FormField>

        <FormField
          label="Relevant Company Policy"
          error={errors.relevantCompanyPolicy}
        >
          <input
            {...register("relevantCompanyPolicy")}
            className={inputClass}
            placeholder="Enter Company Policy"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField label="Witness Details" error={errors.witnessDetails}>
            <input
              {...register("witnessDetails")}
              className={inputClass}
              placeholder="Enter Witness Details"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const ResponseRequirements = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Response Requirements
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Explanation Required For"
          required
          error={errors.explanationRequiredFor}
        >
          <input
            {...register("explanationRequiredFor", {
              required: "Explanation required for is required",
            })}
            className={inputClass}
            placeholder="Enter Reason"
          />
        </FormField>

        <DateField
          name="responseDeadline"
          label="Response Deadline"
          required
          control={control}
          errors={errors}
        />

        <FormField
          label="Response Submission To"
          required
          error={errors.responseSubmissionTo}
        >
          <input
            {...register("responseSubmissionTo", {
              required: "Response submission to is required",
            })}
            className={inputClass}
            placeholder="Enter Name & Designation"
          />
        </FormField>

        <FormField label="Response Format" error={errors.responseFormat}>
          <select {...register("responseFormat")} className={inputClass}>
            <option value="">Select Format</option>
            <option value="Written Explanation">Written Explanation</option>
            <option value="Email">Email</option>
            <option value="In-Person Meeting">In-Person Meeting</option>
          </select>
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Meeting or Hearing Details"
            error={errors.meetingOrHearingDetails}
          >
            <textarea
              {...register("meetingOrHearingDetails")}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter Meeting or Hearing Details"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const PossibleAction = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Possible Action
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Action If No Response Is Received"
          required
          error={errors.actionIfNoResponse}
        >
          <select
            {...register("actionIfNoResponse", {
              required: "Action if no response is required",
            })}
            className={inputClass}
          >
            <option value="">Select Action</option>
            <option value="Proceed Without Explanation">
              Proceed Without Explanation
            </option>
            <option value="Issue a Written Warning">
              Issue a Written Warning
            </option>
            <option value="Initiate Disciplinary Proceedings">
              Initiate Disciplinary Proceedings
            </option>
            <option value="Refer to Disciplinary Committee">
              Refer to Disciplinary Committee
            </option>
          </select>
        </FormField>

        <FormField
          label="Possible Disciplinary Action"
          error={errors.possibleDisciplinaryAction}
        >
          <input
            {...register("possibleDisciplinaryAction")}
            className={inputClass}
            placeholder="Enter Disciplinary Action"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Additional Instructions"
            error={errors.additionalInstructions}
          >
            <textarea
              {...register("additionalInstructions")}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter Instructions"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const AuthorisationDetails = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Authorisation Details
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Issued By" required error={errors.issuedBy}>
          <input
            {...register("issuedBy", {
              required: "Issued by is required",
            })}
            className={inputClass}
            placeholder="Enter Name"
          />
        </FormField>

        <FormField
          label="Issuer Designation"
          required
          error={errors.issuerDesignation}
        >
          <input
            {...register("issuerDesignation", {
              required: "Issuer designation is required",
            })}
            className={inputClass}
            placeholder="Enter Designation"
          />
        </FormField>

        <FormField label="Place of Issue" error={errors.placeOfIssue}>
          <input
            {...register("placeOfIssue")}
            className={inputClass}
            placeholder="Enter Place of Issue"
          />
        </FormField>

        <FormField label="Delivery Method" error={errors.deliveryMethod}>
          <select {...register("deliveryMethod")} className={inputClass}>
            <option value="">Select Method</option>
            <option value="Email">Email</option>
            <option value="Hand Delivery">Hand Delivery</option>
            <option value="Registered Post">Registered Post</option>
            <option value="Courier">Courier</option>
          </select>
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Employee Acknowledgement Required"
            error={errors.acknowledgementRequired}
          >
            <select
              {...register("acknowledgementRequired")}
              className={inputClass}
            >
              <option value="">Yes / No</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </FormField>
        </div>
      </div>
    </div>
  );
};

const ShowCauseNoticePreview = ({ values, logoPreview }) => {
  const formatDate = (date) => {
    if (!date) return "24 August, 2026";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return date;

    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="rounded-xl border border-white shadow-sm backdrop-blur-2xl p-4">
      <h2 className="mb-5 text-sm font-semibold text-[#10324F]">Preview</h2>

      <div className="mx-auto w-full bg-white">
        <Ribbon position="top" />

        <div className="px-4 py-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Company"
                className="h-8 max-w-25 object-contain"
              />
            ) : (
              <span className="text-lg text-[#10324F]">
                {values.companyName || "Company"}
              </span>
            )}
          </div>

          {/* Heading */}
          <div className="mt-3 text-center">
            <p className="text-xs font-bold text-[#10324F]">
              SHOW CAUSE NOTICE
            </p>
          </div>

          {/* Reference */}
          {values.referenceNumber && (
            <div className="mt-3 text-xs text-[#10324F]">
              <p>
                <strong>Reference No:</strong> {values.referenceNumber}
              </p>
            </div>
          )}

          {/* Recipient */}
          <div className="mt-3 text-xs leading-4 text-[#10324F]">
            <p className="font-semibold">To:</p>

            <p>{values.employeeName || "Employee Name"}</p>

            {values.employeeId && <p>Employee ID: {values.employeeId}</p>}

            <p>{values.residentialAddress || "Employee Address"}</p>
          </div>

          {/* Body */}
          <div className="mt-3 text-xs leading-4 text-[#10324F]">
            <p>Dear {values.employeeName || "Employee Name"},</p>

            <p className="mt-3">
              You are hereby called upon to show cause in respect of a matter
              relating to{" "}
              <strong>{values.noticeCategory || "workplace conduct"}</strong>
              {values.severityLevel && (
                <>
                  , assessed as <strong>{values.severityLevel}</strong>
                </>
              )}
              .
            </p>

            <p className="mt-3">
              An incident was recorded on{" "}
              <strong>{formatDate(values.incidentDate)}</strong>
              {values.incidentLocation && <> at {values.incidentLocation}</>}
              {values.relevantCompanyPolicy && (
                <>
                  , with reference to the{" "}
                  <strong>{values.relevantCompanyPolicy}</strong>
                </>
              )}
              .
            </p>

            {values.incidentDescription && (
              <p className="mt-3">{values.incidentDescription}</p>
            )}

            {values.allegationOrConcern && (
              <p className="mt-3">
                Allegation or concern: {values.allegationOrConcern}
              </p>
            )}

            {values.witnessDetails && (
              <p className="mt-3">Witness: {values.witnessDetails}</p>
            )}

            {values.previousCommunication &&
              values.previousCommunication !== "None" && (
                <p className="mt-3">
                  Previous communication on record:{" "}
                  {values.previousCommunication}.
                </p>
              )}

            {values.explanationRequiredFor && (
              <p className="mt-3">
                You are required to submit a written explanation regarding{" "}
                <strong>{values.explanationRequiredFor}</strong>
                {values.responseDeadline && (
                  <>
                    {" "}
                    on or before{" "}
                    <strong>{formatDate(values.responseDeadline)}</strong>
                  </>
                )}
                {values.responseSubmissionTo && (
                  <>, addressed to {values.responseSubmissionTo}</>
                )}
                {values.responseFormat && <> ({values.responseFormat})</>}.
              </p>
            )}

            {values.meetingOrHearingDetails && (
              <p className="mt-3">{values.meetingOrHearingDetails}</p>
            )}

            {values.actionIfNoResponse && (
              <p className="mt-3">
                If no response is received within the stated period, the company
                may <strong>{values.actionIfNoResponse}</strong>
                {values.possibleDisciplinaryAction && (
                  <>, which may include {values.possibleDisciplinaryAction}</>
                )}
                .
              </p>
            )}

            {values.additionalInstructions && (
              <p className="mt-3">{values.additionalInstructions}</p>
            )}
          </div>

          {/* Place / Date */}
          <div className="mt-3 text-xs text-[#10324F]">
            {values.placeOfIssue && (
              <p>Place of Issue: {values.placeOfIssue}</p>
            )}

            <p className="mt-1">Date: {formatDate(values.noticeDate)}</p>

            {values.deliveryMethod && (
              <p className="mt-1">Delivered via: {values.deliveryMethod}</p>
            )}
          </div>

          {/* Signature */}
          <div className="mt-4 text-xs text-[#10324F]">
            <p>Sincerely,</p>

            <p className="mt-4 font-semibold">
              {values.issuedBy || "Authorised Signatory"}
            </p>

            <p>{values.issuerDesignation || "HR Manager"}</p>

            <p>{values.companyName || "Company Name"}</p>
          </div>

          {/* Acknowledgement */}
          {values.acknowledgementRequired === "Yes" && (
            <div className="mt-3 text-xs leading-4 text-[#10324F]">
              <p className="font-semibold">Employee Acknowledgement</p>

              <p className="mt-1">
                I acknowledge receipt of this show cause notice and understand
                its contents.
              </p>

              <p className="mt-3">Signature: ____________ Date: ____________</p>
            </div>
          )}

          <div className="mt-5 text-center text-xs text-[#10324F]">
            Powered by Waggex
          </div>
        </div>

        <Ribbon position="bottom" />
      </div>
    </div>
  );
};

const Ribbon = ({ position = "top" }) => {
  const isTop = position === "top";

  return (
    <div className="relative -mx-px h-9.25 w-[calc(100%+2px)] overflow-hidden">
      {/* Green base */}
      <div className="absolute -inset-0.5 bg-[#10324F]" />

      <div
        className="absolute -inset-0.5"
        style={{
          transform: isTop ? "none" : "scaleX(-1) scaleY(-1)",
        }}
      >
        {/* Magenta ribbon */}
        <div
          className="absolute inset-0 bg-[#D1DFEA]"
          style={{
            clipPath:
              "polygon(76% 0%, 100% 0%, 100% 100%, 51% 100%, 47% 60%, 70% 55%)",
          }}
        />

        {/* White cut / separation */}
        <div
          className="absolute bottom-0 left-0 h-4 w-[55%] bg-white"
          style={{
            clipPath: "polygon(0 0, 88% 0, 100% 100%, 0 100%)",
          }}
        />
      </div>
    </div>
  );
};
